import { Outlet, Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import api from '../utils/api';
import { 
  LayoutDashboard, 
  Building2, 
  Calendar, 
  Users, 
  Briefcase,
  Wrench,
  Mail,
  LogOut,
  Menu,
  X,
  Settings,
  FileText,
  ClipboardList,
  FolderOpen,
  RefreshCw,
  Network,
  Snowflake,
  ChevronRight,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import {
  installWindowScrollGuard,
  setScrollRouteKey,
} from '../utils/windowScrollGuard';
import TodoListModal from './TodoListModal';
import LinkStatusBar from './LinkStatusBar';
import { PageChromeProvider, usePageChrome } from './PageChrome';
import { NavigationOriginProvider } from './NavigationOrigin';
import RouteErrorBoundary from './RouteErrorBoundary';
import {
  getCachedScheduleSettings,
  getCachedTodoDismissedDate,
  loadUserPreferences,
} from '../utils/userPreferences';

/** Building SS ledger and one SS visit stay under Buildings, not the company Inspections queue. */
function isBuildingWorkPath(pathname) {
  return /^\/jobs\/building\/[^/]+/.test(pathname || '') || /^\/jobs\/\d+(?:\/|$)/.test(pathname || '');
}

/** Match sidebar href (path + optional query) to the current location. */
function isHrefActive(href, location, { exactPath = false } = {}) {
  if (!href) return false;
  const [path, query = ''] = href.split('?');
  if (path === '/') return location.pathname === '/';
  const pathOk = exactPath
    ? location.pathname === path
    : location.pathname === path || location.pathname.startsWith(`${path}/`);
  if (!pathOk) return false;
  if (!query) {
    if (exactPath) {
      // Hub links without `?tab=` must not stay active when a tab query is present
      return location.pathname === path && ![...new URLSearchParams(location.search || '')].length;
    }
    return pathOk;
  }
  const want = new URLSearchParams(query);
  const have = new URLSearchParams(location.search);
  let ok = true;
  want.forEach((v, k) => {
    if (have.get(k) !== v) ok = false;
  });
  return ok;
}

/** Sidebar / mobile: health check ?? 503 is a valid JSON body from API, not an axios “network” error */
function DbHealthPanel({
  dbHealth,
  dbHealthError,
  dbHealthErr,
  dbHealthPending,
  dbHealthFetching,
  refetchDbHealth,
  variant,
}) {
  if (dbHealthError) {
    const msg = dbHealthErr?.message || '';
    const timedOut = String(msg).toLowerCase().includes('timeout');
    return (
      <div
        className={`rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-950 ${
          variant === 'sidebar' ? 'px-3 py-2' : 'mb-4 px-3 py-2'
        }`}
      >
        <p className="font-semibold">Cannot reach BWOF API</p>
        <p className="mt-1 text-amber-900/90">
          {timedOut
            ? 'Health check timed out. Start or restart the API in web/backend (npm run dev), then Retry.'
            : 'The browser could not get /api/health/db. Start the API on port 3000, open the app from the Vite URL shown in the terminal (same machine), then Retry.'}
        </p>
        {msg ? <p className="mt-1 font-mono text-[10px] opacity-80 break-all">{msg}</p> : null}
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" onClick={() => refetchDbHealth()} className="font-medium text-primary-700 hover:underline">
            {variant === 'sidebar' ? 'Check again' : 'Retry'}
          </button>
          {variant === 'sidebar' ? (
            <Link to="/settings/db" className="font-medium text-primary-700 hover:underline">
              DB settings
            </Link>
          ) : null}
        </div>
      </div>
    );
  }
  if (dbHealthPending) {
    return variant === 'sidebar' ? (
      <p className="text-xs text-gray-500">Checking database connection…</p>
    ) : null;
  }
  if (dbHealth?.db === 'error') {
    return (
      <div
        className={`rounded-lg border border-red-200 bg-red-50 text-xs text-red-900 ${
          variant === 'sidebar' ? 'px-3 py-2' : 'mb-4 px-3 py-2'
        }`}
      >
        <p className="font-semibold">Database unreachable</p>
        {dbHealth.hint ? <p className="mt-1 text-red-800/90">{dbHealth.hint}</p> : null}
        {!dbHealth.hint && dbHealth.message ? (
          <p className="mt-1 text-red-800/90 font-mono text-[11px] break-all">{dbHealth.message}</p>
        ) : null}
        {dbHealth.code ? (
          <p className="mt-1 text-[10px] text-red-800/80 font-mono">Code: {dbHealth.code}</p>
        ) : null}
        <p className="mt-1 text-red-800/85">
          API is using{' '}
          <span className="font-mono">
            {dbHealth.configuredHost ?? '?'}.{dbHealth.configuredPort ?? '?'} / {dbHealth.configuredDatabase ?? '?'}
          </span>
          . Fix <code className="bg-red-100/80 px-0.5 rounded">web/backend/.env</code>, start MySQL, restart the API.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" onClick={() => refetchDbHealth()} className="font-medium text-primary-700 hover:underline">
            {variant === 'sidebar' ? 'Check again' : 'Retry'}
          </button>
          {variant === 'sidebar' ? (
            <Link to="/settings/db" className="font-medium text-primary-700 hover:underline">
              DB settings
            </Link>
          ) : null}
        </div>
      </div>
    );
  }
  if (dbHealth?.db === 'ok') {
    return variant === 'sidebar' ? (
      <p className="text-xs text-gray-500" title="From GET /api/health/db (live ping)">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5 align-middle" aria-hidden />
        DB: <span className="font-mono text-gray-700">{dbHealth.database}</span>
        <span className="text-gray-400"> @ {dbHealth.host}</span>
        {dbHealthFetching ? <span className="text-gray-400 ml-1">(refreshing…)</span> : null}
      </p>
    ) : null;
  }
  return null;
}

function Layout() {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const {
    data: dbHealth,
    isError: dbHealthError,
    error: dbHealthErr,
    isPending: dbHealthPending,
    isFetching: dbHealthFetching,
    refetch: refetchDbHealth,
  } = useQuery({
    queryKey: ['health', 'db'],
    queryFn: async ({ signal }) => {
      const res = await api.get('/health/db', {
        signal,
        skipErrorToast: true,
        validateStatus: (status) => status === 200 || status === 503,
      });
      if (res.status === 503) {
        const d = res.data || {};
        return {
          db: 'error',
          message: d.message,
          hint: d.hint,
          code: d.code,
          configuredDatabase: d.configuredDatabase,
          configuredHost: d.configuredHost,
          configuredPort: d.configuredPort,
        };
      }
      return res.data;
    },
    refetchInterval: 45_000,
    retry: 1,
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTodoList, setShowTodoList] = useState(false);
  const [todoSettings, setTodoSettings] = useState(() => {
    const s = getCachedScheduleSettings();
    return {
      showTodoList: s.showTodoList !== false,
      todoDaysThreshold: s.todoDaysThreshold || 60,
      dontShowToday: s.dontShowToday !== false,
    };
  });
  const [prefsReady, setPrefsReady] = useState(false);

  // Load schedule / to-do prefs from server (shared across browsers & devices)
  useEffect(() => {
    if (!user) {
      setPrefsReady(false);
      return undefined;
    }
    let cancelled = false;
    loadUserPreferences().then((prefs) => {
      if (cancelled) return;
      const settings = prefs?.scheduleSettings || getCachedScheduleSettings();
      setTodoSettings({
        showTodoList: settings.showTodoList !== false,
        todoDaysThreshold: settings.todoDaysThreshold || 60,
        dontShowToday: settings.dontShowToday !== false,
      });
      setPrefsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Show todo list on mount if enabled (after prefs loaded)
  useEffect(() => {
    if (!user || !prefsReady || !todoSettings.showTodoList) return undefined;

    const dismissedDate = getCachedTodoDismissedDate();
    const today = new Date().toDateString();

    if (dismissedDate !== today) {
      const timer = setTimeout(() => {
        setShowTodoList(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [user, prefsReady, todoSettings.showTodoList]);

  // Listen for settings changes (this tab or another)
  useEffect(() => {
    const handleSettingsChange = () => {
      const settings = getCachedScheduleSettings();
      setTodoSettings({
        showTodoList: settings.showTodoList !== false,
        todoDaysThreshold: settings.todoDaysThreshold || 60,
        dontShowToday: settings.dontShowToday !== false,
      });
    };

    window.addEventListener('scheduleSettingsChanged', handleSettingsChange);
    window.addEventListener('storage', handleSettingsChange);

    return () => {
      window.removeEventListener('scheduleSettingsChanged', handleSettingsChange);
      window.removeEventListener('storage', handleSettingsChange);
    };
  }, []);

  const companyId = user?.company_id || user?.companyId || 'company-001';
  const { data: chillerStatus } = useQuery({
    queryKey: ['chiller', 'status', companyId],
    queryFn: async () =>
      (await api.get('/chiller/status', { params: { companyId } })).data?.data,
    staleTime: 60_000,
    retry: false,
  });
  const chillerEnabled = !!chillerStatus?.chiller_module_enabled;

  const mainNavigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Schedule', href: '/schedule', icon: Calendar },
    { name: 'Buildings', href: '/buildings', icon: Building2, children: [
      { name: 'All', href: '/buildings' },
      { name: 'Full agent', href: '/buildings?scope=full' },
      { name: 'Partly SS only', href: '/buildings?scope=ss_only' },
      { name: 'New building', href: '/buildings/new' },
      { name: 'Starts later', href: '/buildings?contract=deferred' },
      { name: 'Ended', href: '/buildings?contract=ended' },
      { name: 'Inactived list', href: '/buildings?inactive=1' },
    ]},
    ...(chillerEnabled ? [{ name: 'Chiller', href: '/chiller', icon: Snowflake }] : []),
  ];

  const workNavigation =
    user?.role !== 'WORKER'
      ? [
          { name: 'Inspections', href: '/jobs', icon: Briefcase },
          {
            name: 'Paperwork',
            href: '/paperwork',
            icon: ClipboardList,
            children: [
              { name: 'Overview', href: '/paperwork' },
              { name: 'Working', href: '/paperwork/working' },
              { name: 'Council processing', href: '/paperwork?queue=issue_waiting' },
              { name: 'Draft paper', href: '/paperwork?queue=draft_paper' },
              { name: 'Defect stuck', href: '/paperwork?queue=defect_stuck' },
              { name: 'Ready to submit', href: '/paperwork?queue=file_ready' },
            ],
          },
          { name: 'Issued document', href: '/mail', icon: Mail },
          { name: 'Repairing', href: '/repairing', icon: Wrench },
          { name: 'Staff', href: '/people', icon: Users },
          {
            name: 'Registered Company',
            href: '/network',
            icon: Network,
            children: [
              { name: 'Overview', href: '/network' },
              { name: 'Our Partner', href: '/network?tab=our-partner' },
              { name: 'Partly agent', href: '/network?tab=partly-agent' },
              { name: 'Agent', href: '/network?tab=agent' },
              { name: 'Registered IQP', href: '/network?tab=iqp' },
              { name: 'Engineers', href: '/network?tab=engineers' },
            ],
          },
        ]
      : [
          { name: 'Issued document', href: '/mail', icon: Mail },
          { name: 'Repairing', href: '/repairing', icon: Wrench },
        ];

  const footerNavigation = [
    {
      name: 'Application',
      href: '/application',
      icon: FileText,
      children: [
        { name: 'Overview', href: '/application' },
        { name: 'Apply BWOF', href: '/application/apply-bwof' },
        { name: 'Amend CS List', href: '/application/amend-compliance-schedules' },
      ],
    },
    ...(user?.role !== 'WORKER'
      ? [{ name: 'Company files', href: '/documents', icon: FolderOpen }]
      : []),
    {
      name: 'Settings',
      href: '/settings',
      icon: Settings,
      ...(user?.role !== 'WORKER'
        ? {
            children: [
              { name: 'Overview', href: '/settings' },
              { name: 'Checklist templates', href: '/settings/checklists' },
              { name: 'Mail', href: '/settings/mail' },
              { name: 'DB Setting', href: '/settings/db' },
            ],
          }
        : {}),
    },
  ];

  const isNavActive = (href) => {
    if (href === '/jobs' && isBuildingWorkPath(location.pathname)) return false;
    return isHrefActive(href, location);
  };

  const isParentActive = (item) => {
    if (item.href === '/buildings' && isBuildingWorkPath(location.pathname)) return true;
    if (item.children?.some((c) => isHrefActive(c.href, location, { exactPath: true }))) {
      return true;
    }
    if (!item.href) return false;
    if (item.href === '/') return location.pathname === '/';
    return (
      location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
    );
  };

  /** Chevron `>` only hints that in-page tabs/sub-routes exist — no sidebar expand. */
  const renderNavItem = (item, onClick) => {
    const Icon = item.icon;
    const hasSubMenu = Array.isArray(item.children) && item.children.length > 0;
    const active = hasSubMenu ? isParentActive(item) : isNavActive(item.href);

    return (
      <Link
        key={item.name}
        to={item.href}
        state={{ navRoot: true }}
        onClick={onClick}
        title={hasSubMenu ? 'Has sub-menu on the page' : undefined}
        className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
          active ? 'bg-primary-100 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <Icon className="w-5 h-5 mr-3 shrink-0" />
        <span className="truncate flex-1">{item.name}</span>
        {hasSubMenu ? (
          <ChevronRight className="w-4 h-4 ml-1 shrink-0 text-gray-400" aria-hidden />
        ) : null}
      </Link>
    );
  };

  const renderNavigation = (onItemClick) => (
    <>
      {mainNavigation.map((item) => renderNavItem(item, onItemClick))}
      {workNavigation.map((item) => renderNavItem(item, onItemClick))}
      {footerNavigation.map((item) => renderNavItem(item, onItemClick))}
    </>
  );

  const handleLogout = async () => {
    await logout();
  };

  return (
    <PageChromeProvider>
      <NavigationOriginProvider>
        <LayoutShell
        user={user}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        renderNavigation={renderNavigation}
        handleLogout={handleLogout}
        dbHealth={dbHealth}
        dbHealthError={dbHealthError}
        dbHealthErr={dbHealthErr}
        dbHealthPending={dbHealthPending}
        dbHealthFetching={dbHealthFetching}
        refetchDbHealth={refetchDbHealth}
        showTodoList={showTodoList}
        setShowTodoList={setShowTodoList}
        todoSettings={todoSettings}
      />
      </NavigationOriginProvider>
    </PageChromeProvider>
  );
}

function LayoutShell({
  user,
  sidebarOpen,
  setSidebarOpen,
  renderNavigation,
  handleLogout,
  dbHealth,
  dbHealthError,
  dbHealthErr,
  dbHealthPending,
  dbHealthFetching,
  refetchDbHealth,
  showTodoList,
  setShowTodoList,
  todoSettings,
}) {
  const { setMainNode, setSubNavNode, setTabsNode, hasHeading } = usePageChrome();
  const location = useLocation();

  useEffect(() => installWindowScrollGuard(), []);

  // Only pathname resets scroll — ?tab= / filters must not jump to top.
  useEffect(() => {
    setScrollRouteKey(location.pathname);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white shadow-xl">
          <div className="shrink-0 bg-navy-900 text-white border-b border-navy-950/40 shadow-md">
            <div className="flex h-14 items-center justify-between px-4">
              <h1 className="text-lg font-bold tracking-tight">BWOF Admin</h1>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex h-10 items-center px-4 border-t border-white/10">
              <p className="text-[11px] text-white/55 leading-none">© Cornerstone Building Service Ltd</p>
            </div>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {renderNavigation(() => setSidebarOpen(false))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="shrink-0 bg-navy-900 text-white border-b border-navy-950/40 shadow-md">
            <div className="flex h-14 items-center px-4">
              <h1 className="text-lg font-bold tracking-tight">BWOF Admin</h1>
            </div>
            <div className="flex h-10 items-center px-4 border-t border-white/10">
              <p className="text-[11px] text-white/55 leading-none">© Cornerstone Building Service Ltd</p>
            </div>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {renderNavigation()}
          </nav>
          <div className="p-4 border-t space-y-3">
            <LinkStatusBar user={user} />
            <DbHealthPanel
              dbHealth={dbHealth}
              dbHealthError={dbHealthError}
              dbHealthErr={dbHealthErr}
              dbHealthPending={dbHealthPending}
              dbHealthFetching={dbHealthFetching}
              refetchDbHealth={refetchDbHealth}
              variant="sidebar"
            />
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Full page reload (same as Ctrl+R)"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload page
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        <div className="sticky top-0 z-10 bg-navy-900 text-white shadow-md border-b border-navy-950/40">
          <div className="flex h-14 shrink-0 items-center gap-3 px-3 lg:px-6 bg-navy-900 border-b border-navy-950/40">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="rounded-lg p-1.5 text-white/85 hover:bg-white/10 hover:text-white lg:hidden shrink-0"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex items-center min-w-0 flex-1 h-full min-h-0">
              {!hasHeading && (
                <div className="flex items-center gap-2 min-w-0 text-sm h-full">
                  <span className="font-semibold tracking-wide text-white">Cornerstone</span>
                  <span className="text-white/35">·</span>
                  <span className="text-white/70 truncate">BWOF</span>
                </div>
              )}
              <div ref={setMainNode} className="flex items-center min-w-0 flex-1 h-full min-h-0" />
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <LinkStatusBar user={user} tone="dark" />
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="text-white/75 p-2 rounded-lg hover:bg-white/10 hover:text-white hidden sm:inline-flex"
                title="Reload page"
                aria-label="Reload page"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="text-white/75 p-2 rounded-lg hover:bg-white/10 hover:text-white"
                aria-label="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div
            ref={setSubNavNode}
            className="h-10 shrink-0 flex items-center px-3 lg:px-6 bg-navy-900 border-b border-white/10 min-w-0"
          />
          <div ref={setTabsNode} />
        </div>

        <main className="p-4 lg:p-8">
          <div className="lg:hidden">
            <DbHealthPanel
              dbHealth={dbHealth}
              dbHealthError={dbHealthError}
              dbHealthErr={dbHealthErr}
              dbHealthPending={dbHealthPending}
              dbHealthFetching={dbHealthFetching}
              refetchDbHealth={refetchDbHealth}
              variant="mobile"
            />
          </div>
          <RouteErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </RouteErrorBoundary>
        </main>
      </div>

      {showTodoList && (
        <TodoListModal onClose={() => setShowTodoList(false)} settings={todoSettings} />
      )}
    </div>
  );
}

export default Layout;
